import React, {
  forwardRef,
  useRef,
  useState,
  useEffect,
  TextareaHTMLAttributes,
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
} from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '../../utils'

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  autoResize?: boolean
  debounce?: number
  disabled?: boolean
  size?: 'medium' | 'large' | 'xxlarge'
}

const textareaVariants = cva(
  'bg-element placeholder:text-foreground-placeholder text-foreground focus:border-foreground/60 focus:bg-transparent resize-none rounded-xl border font-sans text-sm transition-[padding] duration-200 ease-in-out',
  {
    variants: {
      size: {
        medium: 'px-3 py-2',
        large: 'px-4 py-2 text-md',
        xxlarge: 'p-5 text-lg',
      },
      disabled: {
        true: 'pointer-events-none opacity-30',
        false: '',
      },
      labelVisible: {
        true: 'pb-2 pt-8', // Adjust padding when label is shown (xxlarge)
        false: '',
      },
    },
    defaultVariants: {
      size: 'medium',
      disabled: false,
      labelVisible: false,
    },
  },
)

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      error,
      autoResize,
      debounce,
      size = 'medium',
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState<string>(
      value !== undefined ? String(value) : ((defaultValue as string) ?? ''),
    )
    const [showLabel, setShowLabel] = useState<boolean>(!!inputValue)
    const textareaRef = useRef<HTMLTextAreaElement | null>(null)
    const debounceTimeoutRef = useRef<number | undefined>(undefined)

    const adjustHeight = () => {
      if (textareaRef.current && autoResize) {
        const textarea = textareaRef.current
        textarea.style.height = 'auto'
        textarea.style.height = `${textarea.scrollHeight + 3}px`
      }
    }

    const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
      if (value !== undefined) {
        // Controlled component
        onChange?.(e)
      } else {
        // Uncontrolled component with optional debounce
        setInputValue(e.target.value)
        if (typeof debounce === 'number') {
          if (debounceTimeoutRef.current) {
            window.clearTimeout(debounceTimeoutRef.current)
          }
          debounceTimeoutRef.current = window.setTimeout(() => {
            onChange?.(e)
          }, debounce)
        } else {
          onChange?.(e)
        }
      }

      if (autoResize) {
        adjustHeight()
      }
    }

    const handleFocus: FocusEventHandler<HTMLTextAreaElement> = (e) => {
      onFocus?.(e)
      // Show label on focus if size is xxlarge (mimicking original behavior)
      if (size === 'xxlarge') {
        setShowLabel(true)
      }
    }

    const handleBlur: FocusEventHandler<HTMLTextAreaElement> = (e) => {
      onBlur?.(e)
      // Hide label if empty
      if (size === 'xxlarge') {
        setShowLabel(!!e.target.value.length)
      }
    }

    const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
      onKeyDown?.(e)
    }

    useEffect(() => {
      adjustHeight()
      // If value changes externally in a controlled scenario, or inputValue changes for uncontrolled.
    }, [autoResize, value, inputValue])

    return (
      <div className="relative flex flex-col gap-3">
        {label && size === 'xxlarge' && (
          <span
            className={cn(
              'text-light-20 pointer-events-none absolute left-[20px] top-[10px] h-full w-full text-[13px] opacity-0 transition-opacity duration-200 ease-in-out',
              showLabel && 'opacity-100',
            )}
          >
            {label}
          </span>
        )}
        {label && size !== 'xxlarge' && <span className="text-light text-[15px] font-normal">{label}</span>}

        <textarea
          ref={(node) => {
            textareaRef.current = node
            if (typeof ref === 'function') ref(node)
            else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node
          }}
          value={value !== undefined ? value : inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className={cn(
            textareaVariants({
              size,
              disabled: !!disabled,
              labelVisible: size === 'xxlarge' && showLabel,
            }),
          )}
          {...rest}
        />

        {error && <span className="text-[#FF4D4D]">{error}</span>}
      </div>
    )
  },
)

TextArea.displayName = 'TextArea'
